import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import {
  AlertComponent,
  DialogComponent,
  OperatingHoursComponent,
} from '@components';
import { Status } from '@enums';
import { Customer } from '@models';
import { UtilService } from '@services';
import {
  fetchCurrentCustomer,
  selectCurrentCustomer,
  selectCustomersFetchedAt,
  updateCustomer,
  updateCustomerFailed,
  updateCustomerImage,
  updateCustomerImageFailed,
  updateCustomerImageSuccess,
  updateCustomerSuccess,
} from '@state';
import { defaultOperatingHours, OperatingHours } from '@types';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSlideToggleModule,
    AlertComponent,
    OperatingHoursComponent,
  ],
  templateUrl: './customer.page.html',
  styleUrl: './customer.page.scss',
})
export class CustomerPage implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);

  public loading: boolean = false;
  public error: string | null = null;
  public customer: Customer = new Customer();
  public operatingHours: OperatingHours = { ...defaultOperatingHours };
  private sub: Subscription | null = null;

  public $fetchedAt = this.store.select(selectCustomersFetchedAt);
  private $customer = this.store.select(selectCurrentCustomer);

  public form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    enabled: new FormControl(false, [Validators.required]),
  });

  constructor(private store: Store, public utils: UtilService) {}

  async ngOnInit(): Promise<void> {
    this.store.dispatch(fetchCurrentCustomer());

    this.sub = this.$customer.subscribe((customer) => {
      if (!customer) return;
      this.customer = customer;
      if (customer.operatingHours) {
        this.operatingHours = { ...customer.operatingHours };
      }

      this.populateForm(customer);
    });
  }

  private populateForm(customer: Customer): void {
    this.form.patchValue({
      name: customer.name,
      description: customer.description,
      enabled: customer.status == Status.APPROVED,
    });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  public getFormControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  public async onFormSubmit(): Promise<void> {
    if (!this.form.valid) {
      this.error = 'Please fill in all required fields';
      return;
    }

    this.loading = true;

    try {
      const customer: Customer = {
        ...this.customer,
        name: this.getFormControl('name').value,
        description: this.getFormControl('description').value,
        status: this.getFormControl('enabled').value
          ? Status.APPROVED
          : Status.DECLINED,
        operatingHours: this.operatingHours,
      };

      await this.updateCustomer(customer);

      this.showSuccessModal();
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }

    this.loading = false;
  }

  private async updateCustomer(customer: Customer): Promise<void> {
    await this.utils.actionsToPromise(
      updateCustomer({ customer }),
      updateCustomerSuccess,
      updateCustomerFailed
    );
  }

  private showSuccessModal(): void {
    this.dialog.open(DialogComponent, {
      data: {
        title: 'Success',
        message: 'Customer updated successfully',
      },
    });
  }

  public async onFileChange(event: any): Promise<void> {
    const file = event.target.files[0];
    if (!file) return;

    this.loading = true;
    try {
      await this.utils.actionsToPromise(
        updateCustomerImage({ customer: this.customer, file }),
        updateCustomerImageSuccess,
        updateCustomerImageFailed
      );
      this.showSuccessModal();
    } catch (err: any) {
      this.error = this.utils.getErrorMessage(err);
    }
    this.loading = false;
  }

  public onOperatingHoursChange(operatingHours: OperatingHours): void {
    this.operatingHours = operatingHours;
  }
}
